/**
 * ExitIntentPopup.jsx
 * Dynamic offer popup driven by admin campaigns (GET /api/popup-campaigns/active).
 * Shown only to logged-in users. On claim, sends a separate popup-offer email with coupon (not newsletter).
 * Coupon is visible at checkout until used (one-time per user); then it no longer appears in applicable list.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../context/ToastContext';
import { useAuth } from '../../../context/AuthContext';
import './ExitIntentPopup.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CONFIG = {
  minTimeOnPageSec: 4,
  allowedPaths: ['/', '/product', '/women', '/shop', '/shop/women', '/search', '/new-arrivals', '/bestsellers'],
  blockedPaths: ['/checkout', '/payment', '/order-confirmation', '/order-success', '/cart', '/account', '/login', '/register'],
};

function useFocusTrap(isActive) {
  const ref = useRef(null);
  useEffect(() => {
    if (!isActive || !ref.current) return;
    const el = ref.current;
    const focusable = el.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const trap = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    };
    el.addEventListener('keydown', trap);
    return () => el.removeEventListener('keydown', trap);
  }, [isActive]);
  return ref;
}

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const SpinnerIcon = () => (
  <svg className="eip-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"/>
  </svg>
);

const GiftIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
    <polyline points="20 12 20 22 4 22 4 12"/>
    <rect x="2" y="7" width="20" height="5"/>
    <line x1="12" y1="22" x2="12" y2="7"/>
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
  </svg>
);

function ExitIntentPopup() {
  const { user, token } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [alreadyClaimed, setAlreadyClaimed] = useState(false);
  const [loadingCampaign, setLoadingCampaign] = useState(true);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [couponCode, setCouponCode] = useState(null);

  const popupRef = useFocusTrap(visible);
  const armedRef = useRef(false);
  const shownForCampaignRef = useRef(null);

  const isAllowedPath = useCallback(() => {
    const path = window.location.pathname;
    if (CONFIG.blockedPaths.some((p) => path.startsWith(p))) return false;
    if (CONFIG.allowedPaths.length === 0) return true;
    return CONFIG.allowedPaths.some((p) => path === p || path.startsWith(p + '/'));
  }, []);

  const fetchActiveCampaign = useCallback(async () => {
    setLoadingCampaign(true);
    try {
      const opts = { headers: {} };
      if (token) opts.headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/popup-campaigns/active`, opts);
      const data = await res.json().catch(() => ({}));
      if (data.success && data.campaign) {
        setCampaign(data.campaign);
        setAlreadyClaimed(!!data.alreadyClaimed);
      } else {
        // No active campaigns in admin → campaign stays null → component renders nothing
        setCampaign(null);
        setAlreadyClaimed(false);
      }
    } catch {
      setCampaign(null);
    } finally {
      setLoadingCampaign(false);
    }
  }, [token]);

  useEffect(() => {
    fetchActiveCampaign();
  }, [fetchActiveCampaign]);

  // Time-based auto-show: after a short delay, show popup once per campaign per session (logged-in users only).
  useEffect(() => {
    if (!user) return;
    if (!campaign || alreadyClaimed) return;
    if (!isAllowedPath()) return;
    const sessionKey = `eip_shown_${campaign._id}`;
    if (sessionStorage.getItem(sessionKey)) return;

    const t = setTimeout(() => {
      armedRef.current = true;
      if (shownForCampaignRef.current === campaign._id) return;
      shownForCampaignRef.current = campaign._id;
      sessionStorage.setItem(sessionKey, 'true');
      setVisible(true);
    }, CONFIG.minTimeOnPageSec * 1000);

    return () => clearTimeout(t);
  }, [user, campaign, alreadyClaimed, isAllowedPath]);

  const shouldShowPopup = useCallback(() => {
    if (!user) return false;
    if (!campaign || alreadyClaimed) return false;
    if (shownForCampaignRef.current === campaign._id) return false;
    if (!isAllowedPath()) return false;

    const sessionKey = `eip_shown_${campaign._id}`;
    if (campaign.displayRule === 'first_visit') {
      if (sessionStorage.getItem(sessionKey)) return false;
    }
    if (campaign.displayRule === 'every_visit') {
      if (sessionStorage.getItem(sessionKey)) return false;
    }
    return true;
  }, [user, campaign, alreadyClaimed, isAllowedPath]);

  const triggerPopup = useCallback(() => {
    if (!armedRef.current) return;
    if (!shouldShowPopup()) return;
    shownForCampaignRef.current = campaign._id;
    sessionStorage.setItem(`eip_shown_${campaign._id}`, 'true');
    setVisible(true);
  }, [shouldShowPopup, campaign]);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
    }, 280);
  }, []);

  useEffect(() => {
    if (loadingCampaign || !shouldShowPopup()) return;

    const armTimer = setTimeout(() => {
      armedRef.current = true;
    }, CONFIG.minTimeOnPageSec * 1000);

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) triggerPopup();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') triggerPopup();
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape' && visible) handleClose();
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('keydown', handleEscape);

    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [loadingCampaign, shouldShowPopup, triggerPopup, visible, handleClose]);

  useEffect(() => {
    if (visible) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [visible]);

  const handleClaim = async (e) => {
    e.preventDefault();
    setError('');
    if (!campaign) return;

    if (campaign.requireLogin && !user) {
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      handleClose();
      return;
    }

    setSubmitting(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s — allow for backend cold start

    try {
      const res = await fetch(`${API_URL}/popup-campaigns/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ campaignId: campaign._id }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = data.message || (res.status === 429 ? 'Too many requests. Please try again in a moment.' : 'Something went wrong. Please try again.');
        setError(msg);
        return;
      }

      if (data.couponCode) {
        setCouponCode(data.couponCode);
        try {
          localStorage.setItem('popup_offer_coupon', data.couponCode);
        } catch {}
      }
      setSubmitted(true);
      showToast(data.message || "You're in — check your inbox for details.", 'success');
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        setError('Request took too long. Please check your connection and try again.');
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignInToClaim = () => {
    navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
    handleClose();
  };

  // No active campaign from admin = show nothing (no placeholder, no empty popup)
  if (loadingCampaign || !campaign) return null;
  if (alreadyClaimed) return null;
  if (!user) return null;
  if (!visible) return null;

  const requireLogin = campaign.requireLogin !== false;
  const isLoggedIn = !!user;
  const userEmail = (user && (user.email || user.emailAddress)) || '';

  return (
    <>
      <div
        className={`eip-overlay${closing ? ' eip-overlay--closing' : ''}`}
        onClick={handleClose}
        aria-hidden="true"
      />

      <div
        ref={popupRef}
        className={`eip${closing ? ' eip--closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="eip-title"
        aria-describedby="eip-desc"
      >
        <button
          type="button"
          className="eip__close"
          onClick={handleClose}
          aria-label="Close offer"
        >
          <CloseIcon />
        </button>

        {!submitted ? (
          <>
            <div className="eip__offer-badge" aria-hidden="true">
              <GiftIcon />
            </div>

            <div className="eip__discount" aria-label={campaign.discountLabel}>
              {campaign.discountLabel}
            </div>

            {campaign.discountSubtext && (
              <h2 id="eip-title" className="eip__title">
                {campaign.discountSubtext}
              </h2>
            )}

            <p id="eip-desc" className="eip__subtitle">
              {campaign.description || 'Join our community for exclusive drops, early access to new collections, and style edits curated for you.'}
            </p>

            {requireLogin && !isLoggedIn ? (
              <div className="eip__form">
                <p className="eip__signin-prompt">
                  Sign in to claim this offer. We'll apply the discount to your account.
                </p>
                <button
                  type="button"
                  className="eip__submit"
                  onClick={handleSignInToClaim}
                >
                  Sign in to claim
                </button>
              </div>
            ) : (
              <form onSubmit={handleClaim} className="eip__form" noValidate>
                {isLoggedIn && (
                  <div className="eip__field">
                    <label htmlFor="eip-email" className="eip__sr-only">Email address</label>
                    <input
                      id="eip-email"
                      type="email"
                      value={userEmail}
                      readOnly
                      className="eip__input eip__input--readonly"
                      aria-label="Your account email (used for this offer)"
                    />
                    <span className="eip__email-note">Using your account email</span>
                  </div>
                )}
                {error && (
                  <span className="eip__error" role="alert">{error}</span>
                )}
                <button
                  type="submit"
                  className="eip__submit"
                  disabled={submitting}
                  aria-busy={submitting}
                >
                  {submitting ? <><SpinnerIcon /> Claiming…</> : campaign.ctaText}
                </button>
              </form>
            )}

            <p className="eip__privacy">
              This offer is separate from our newsletter. By claiming you agree to our{' '}
              <a href="/privacy" className="eip__privacy-link" target="_blank" rel="noopener">
                Privacy Policy
              </a>.
            </p>
          </>
        ) : (
          <div className="eip__success">
            <div className="eip__success-icon" aria-hidden="true">🎉</div>
            <h2 className="eip__success-title">You're in!</h2>
            <p className="eip__success-text">
              {couponCode
                ? `Use code ${couponCode} at checkout. We've also sent it to your email.`
                : "Check your email — we've sent your offer."}
            </p>
            <button type="button" className="eip__submit" onClick={handleClose}>
              Start Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default ExitIntentPopup;
